import { panoLabel, panoOrdinals } from "@viewer-labels";

import type { POI, Pin, Frame, PanoramaWithUrl, ReviewerNote } from "../api/types";
import type { TrackSample } from "./track";

/** Escape the five XML metacharacters for safe inclusion in KML text/attrs. */
export function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Convert a CSS `#RRGGBB` colour to KML's opaque `aabbggrr` byte order. */
export function hexToKmlColor(hex: string): string {
  const hex6 = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())?.[1];
  if (hex6 === undefined) return "ffffffff";
  const rr = hex6.slice(0, 2);
  const gg = hex6.slice(2, 4);
  const bb = hex6.slice(4, 6);
  return `ff${bb}${gg}${rr}`.toLowerCase();
}

/** Format an ISO-8601 timestamp to KML's `YYYY-MM-DDTHH:MM:SSZ`. */
export function kmlInstant(iso: string): string {
  return new Date(iso).toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Format a number of seconds as `m:ss` for balloon text. */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export interface KmlBuildInput {
  flightName: string;
  flightId: string;
  captureDate: string;
  videoStartUtc?: string;
  track: TrackSample[];
  pins: Pin[];
  pois: POI[];
  frames: Frame[];
  panoramas: PanoramaWithUrl[];
  /** Logical image key -> relative path inside the KMZ. An absent key means
   *  the image could not be collected; the balloon degrades gracefully. */
  imageHrefs: Map<string, string>;
}

const WHT_ICON = "http://maps.google.com/mapfiles/kml/paddle/wht-blank.png";

// Google Earth's default balloon template includes $[geDirections], which
// renders as the "Directions: To here / From here" footer — links that depend
// on Google's directions service and are functionally dead in Earth Pro.
// Setting a custom <BalloonStyle><text> overrides the default template; by
// omitting $[geDirections] from our template, the footer disappears. We
// preserve $[name] (as the balloon header) and $[description] (our HTML
// balloon body) so the visual structure matches the default.
const BALLOON_STYLE =
  "<BalloonStyle><text>" +
  "<![CDATA[<h3>$[name]</h3>$[description]]]>" +
  "</text></BalloonStyle>";

function notesHtml(notes: ReviewerNote[]): string {
  if (notes.length === 0) return "";
  const lines = notes
    .map((n) => `${escapeXml(n.displayName)} — ${escapeXml(n.text)} ` +
      `(${escapeXml(n.modifiedAt.slice(0, 10))})`)
    .join("<br/>");
  return `<p>${lines}</p>`;
}

/** One balloon image: a preview-sized thumbnail linked to the full-resolution
 *  image, so a click in the Google Earth balloon opens it full size. Shows a
 *  single image (never the thumbnail and full stacked) and degrades when no
 *  image is available. */
function previewHtml(
  thumbHref: string | undefined, fullHref: string | undefined,
): string {
  const shown = thumbHref ?? fullHref;
  if (shown === undefined) return "<p><em>[image unavailable]</em></p>";
  const img = `<img src="${escapeXml(shown)}" width="320"/>`;
  if (fullHref !== undefined) {
    return `<p><a href="${escapeXml(fullHref)}">${img}</a></p>` +
      `<p style="font-size:11px">Click the image to open it full size.</p>`;
  }
  return `<p>${img}</p>`;
}

function tintedStyle(id: string, hex: string): string {
  return `  <Style id="${id}">
    <IconStyle><color>${hexToKmlColor(hex)}</color><scale>1.1</scale>
      <Icon><href>${WHT_ICON}</href></Icon>
    </IconStyle>
    ${BALLOON_STYLE}
  </Style>\n`;
}

function placemark(
  name: string, lat: number, lon: number, styleId: string, balloon: string,
): string {
  return `    <Placemark>
      <name>${escapeXml(name)}</name>
      <styleUrl>#${styleId}</styleUrl>
      <description><![CDATA[${balloon}]]></description>
      <Point><coordinates>${lon.toFixed(7)},${lat.toFixed(7)},0</coordinates></Point>
    </Placemark>\n`;
}

/** A KML PhotoOverlay with shape:sphere — Google Earth Pro wraps the
 *  equirectangular JPEG onto the inside of a sphere so the user can fly into
 *  it and look around 360°, the same Street-View-style experience the web
 *  app's panorama viewer (PSV) gives. */
function panoramaOverlay(
  name: string,
  lat: number, lon: number, altM: number | undefined,
  fullHref: string,
  balloon: string,
): string {
  const altMode = altM !== undefined ? "absolute" : "relativeToGround";
  const alt = altM ?? 2;
  return `    <PhotoOverlay>
      <name>${escapeXml(name)}</name>
      <Style>${BALLOON_STYLE}</Style>
      <description><![CDATA[${balloon}]]></description>
      <Camera>
        <longitude>${lon.toFixed(7)}</longitude>
        <latitude>${lat.toFixed(7)}</latitude>
        <altitude>${alt.toFixed(2)}</altitude>
        <altitudeMode>${altMode}</altitudeMode>
        <heading>0</heading>
        <tilt>90</tilt>
        <roll>0</roll>
      </Camera>
      <Icon><href>${escapeXml(fullHref)}</href></Icon>
      <ViewVolume>
        <leftFov>-180</leftFov>
        <rightFov>180</rightFov>
        <bottomFov>-90</bottomFov>
        <topFov>90</topFov>
        <!-- The sphere's radius from the camera. Must be large enough that
             the sphere is non-degenerate or Google Earth silently declines to
             render it; matches Google's own KML PhotoOverlay docs example. -->
        <near>1000</near>
      </ViewVolume>
      <shape>sphere</shape>
      <Point><coordinates>${lon.toFixed(7)},${lat.toFixed(7)},0</coordinates></Point>
    </PhotoOverlay>\n`;
}

export function buildKmlDocument(input: KmlBuildInput): string {
  const pathCoords = input.track
    .map((s) => `${s.lon.toFixed(7)},${s.lat.toFixed(7)},0`).join(" ");

  // gx:Track — one <when>/<gx:coord> pair per sample. Without videoStartUtc
  // the track still draws but cannot ride the GE timeline.
  const startMs = input.videoStartUtc !== undefined
    ? new Date(input.videoStartUtc).getTime() : null;
  const whens = startMs === null ? "" : input.track
    .map((s) => `        <when>` +
      `${kmlInstant(new Date(startMs + s.t * 1000).toISOString())}</when>`)
    .join("\n");
  const coords = input.track
    .map((s) => `        <gx:coord>${s.lon.toFixed(7)} ${s.lat.toFixed(7)} 0</gx:coord>`)
    .join("\n");

  // Collect per-marker styles so each placemark can carry its own colour.
  const styles: string[] = [];
  const pinMarks = input.pins.map((p) => {
    const sid = `pin-${escapeXml(p.id)}`;
    styles.push(tintedStyle(sid, p.color));
    return placemark(p.name, p.lat, p.lon, sid, notesHtml(p.notes));
  }).join("");
  const poiMarks = input.pois.map((p) => {
    const sid = `poi-${escapeXml(p.id)}`;
    styles.push(tintedStyle(sid, p.color));
    const balloon = `<p>Video time ${formatClock(p.t)}</p>` +
      previewHtml(
        input.imageHrefs.get(`poi-thumb:${p.id}`),
        input.imageHrefs.get(`poi-full:${p.id}`),
      ) + notesHtml(p.notes);
    return placemark(p.name, p.lat, p.lon, sid, balloon);
  }).join("");
  const frameMarks = input.frames.map((f, i) => {
    const sid = `frame-${escapeXml(f.id)}`;
    styles.push(tintedStyle(sid, "#ffd000"));
    const balloon =
      `<p>Captured by ${escapeXml(f.capturedBy.displayName)} ` +
      `at video time ${formatClock(f.videoTimeS)}</p>` +
      (f.caption !== undefined ? `<p>${escapeXml(f.caption)}</p>` : "") +
      previewHtml(
        input.imageHrefs.get(`frame-thumb:${f.id}`),
        input.imageHrefs.get(`frame-full:${f.id}`),
      );
    // Auto-named frames carry a unique index — many can share a video time.
    const name = f.caption ?? `Frame ${i + 1} (${formatClock(f.videoTimeS)})`;
    return placemark(name, f.lat, f.lon, sid, balloon);
  }).join("");
  // No track-snap time is available here, so labels are the explicit name /
  // prettified id / "Pano N" — never the viewer's " — m:ss" time suffix.
  const panoOrds = panoOrdinals(input.panoramas);
  const panoMarks = input.panoramas.map((p) => {
    const fullHref = input.imageHrefs.get(`pano-full:${p.id}`);
    const thumbHref = input.imageHrefs.get(`pano-thumb:${p.id}`);
    if (fullHref === undefined) {
      // No equirectangular image to wrap onto a sphere — fall back to a flat
      // placemark so the panorama still appears on the map.
      const sid = `pano-${escapeXml(p.id)}`;
      styles.push(tintedStyle(sid, "#00d0a0"));
      return placemark(
        panoLabel(p, panoOrds.get(p.id) ?? 1, null),
        p.lat, p.lon, sid, previewHtml(thumbHref, undefined),
      );
    }
    const balloon =
      "<p><strong>Double-click the camera icon on the map</strong> to fly " +
      "into the panorama (look around in 3D, press Esc to exit).</p>" +
      previewHtml(thumbHref, fullHref);
    return panoramaOverlay(
      panoLabel(p, panoOrds.get(p.id) ?? 1, null),
      p.lat, p.lon, p.altM, fullHref, balloon,
    );
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2"
     xmlns:gx="http://www.google.com/kml/ext/2.2">
<Document>
  <name>${escapeXml(input.flightName)}</name>
  <open>1</open>
  <description>video-map-cloud — annotated export. ` +
    `Flight ${escapeXml(input.flightId)} (${escapeXml(input.captureDate)}).</description>
  <Style id="flightLine">
    <LineStyle><color>ff0000ff</color><width>2.5</width></LineStyle>
    ${BALLOON_STYLE}
  </Style>
  <Style id="heliStyle">
    <IconStyle><scale>1.3</scale>
      <Icon><href>http://maps.google.com/mapfiles/kml/paddle/ylw-circle.png</href></Icon>
      <hotSpot x="0.5" y="0.5" xunits="fraction" yunits="fraction"/>
    </IconStyle>
    ${BALLOON_STYLE}
  </Style>
${styles.join("")}  <Folder><name>Flight Path</name><visibility>1</visibility>
    <Placemark><name>Flight path</name><styleUrl>#flightLine</styleUrl>
      <LineString><tessellate>1</tessellate><coordinates>${pathCoords}</coordinates></LineString>
    </Placemark>
  </Folder>
  <Folder><name>Heli</name><visibility>1</visibility>
    <Placemark><name>Helicopter</name><styleUrl>#heliStyle</styleUrl>
      <gx:Track>
        <altitudeMode>clampToGround</altitudeMode>
${whens}
${coords}
      </gx:Track>
    </Placemark>
  </Folder>
  <Folder><name>Pins</name><visibility>1</visibility>
${pinMarks}  </Folder>
  <Folder><name>POIs</name><visibility>1</visibility>
${poiMarks}  </Folder>
  <Folder><name>Saved Frames</name><visibility>1</visibility>
${frameMarks}  </Folder>
  <Folder><name>Panoramas</name><visibility>1</visibility>
${panoMarks}  </Folder>
</Document>
</kml>
`;
}
